import { getLocale, t, useI18n } from '../../i18n';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  leaveService,
  type GuardOption,
  type LeaveRequest,
  type LeaveStatus,
  type LeaveType,
} from '../../services/leaveService';
import { getAvatarAccentStyle } from '../../utils/avatarAccent';
import './LeaveManagementPage.css';

type PageTab = 'requests' | 'add';

const STATUS_OPTIONS: { value: LeaveStatus | ''; label: string }[] = [
  { value: '', label: 'All statuses' },
  { value: 'pending', label: 'Pending' },
  { value: 'approved', label: 'Approved' },
  { value: 'denied', label: 'Denied' },
  { value: 'cancelled', label: 'Cancelled' },
];

const LEAVE_TYPE_LABELS: Record<LeaveType, string> = {
  annual: 'Annual leave',
  sick: 'Sick leave',
  unpaid: 'Unpaid leave',
  other: 'Other',
};

const LEAVE_TYPE_OPTIONS: { value: LeaveType; label: string }[] = [
  { value: 'annual', label: 'Annual leave' },
  { value: 'sick', label: 'Sick leave' },
  { value: 'unpaid', label: 'Unpaid leave' },
  { value: 'other', label: 'Other' },
];

const STATUS_BADGE_CLASS: Record<LeaveStatus, string> = {
  pending: 'leave-page__badge--pending',
  approved: 'leave-page__badge--approved',
  denied: 'leave-page__badge--denied',
  cancelled: 'leave-page__badge--cancelled',
};

const normaliseSearch = (value: string): string => value.trim().toLowerCase();

const initials = (name: string): string =>
  name
    .split(' ')
    .slice(0, 2)
    .map((word) => word[0] ?? '')
    .join('')
    .toUpperCase();

function formatDateRange(start: string, end: string): string {
  const startDate = new Date(start + 'T00:00:00');
  const endDate = new Date(end + 'T00:00:00');
  const fmt = new Intl.DateTimeFormat(getLocale(), { month: 'short', day: 'numeric', year: 'numeric' });

  if (start === end) return fmt.format(startDate);
  return `${fmt.format(startDate)} – ${fmt.format(endDate)}`;
}

function countDays(start: string, end: string): number {
  const startDate = new Date(start + 'T00:00:00');
  const endDate = new Date(end + 'T00:00:00');
  return Math.round((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)) + 1;
}

function formatRelativeTime(isoString: string): string {
  const date = new Date(isoString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);

  if (diffMins < 1) return t('just now');
  if (diffMins < 60) return t('{count}m ago', { count: diffMins });
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return t('{count}h ago', { count: diffHours });
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 7) return t('{count}d ago', { count: diffDays });

  return new Intl.DateTimeFormat(getLocale(), { month: 'short', day: 'numeric' }).format(date);
}

function todayDateKey(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
}

export default function LeaveManagementPage() {
  useI18n();
  const [activeTab, setActiveTab] = useState<PageTab>('requests');
  const [requests, setRequests] = useState<LeaveRequest[]>([]);
  const [guards, setGuards] = useState<GuardOption[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState<LeaveStatus | ''>('');
  const [guardQuery, setGuardQuery] = useState('');

  // Admin notes state for inline editing on pending requests
  const [editingNotesId, setEditingNotesId] = useState<string | null>(null);
  const [editingNotesValue, setEditingNotesValue] = useState('');

  // Action in-flight tracking
  const [actionInFlight, setActionInFlight] = useState<string | null>(null);

  // Add-leave form state
  const [formGuardId, setFormGuardId] = useState('');
  const [formLeaveType, setFormLeaveType] = useState<LeaveType>('annual');
  const [formStartDate, setFormStartDate] = useState(() => todayDateKey());
  const [formEndDate, setFormEndDate] = useState(() => todayDateKey());
  const [formReason, setFormReason] = useState('');
  const [formAdminNotes, setFormAdminNotes] = useState('');
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [formSuccess, setFormSuccess] = useState('');

  const normalisedGuardQuery = useMemo(() => normaliseSearch(guardQuery), [guardQuery]);

  const loadRequests = useCallback(async () => {
    setIsLoading(true);
    setError('');
    try {
      const rows = await leaveService.fetchLeaveRequests(statusFilter || undefined);
      setRequests(rows);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setIsLoading(false);
    }
  }, [statusFilter]);

  const loadGuards = useCallback(async () => {
    try {
      const rows = await leaveService.fetchGuards();
      setGuards(rows);
    } catch (err) {
      setError((err as Error).message);
    }
  }, []);

  useEffect(() => {
    void loadRequests();
  }, [loadRequests]);

  useEffect(() => {
    void loadGuards();
  }, [loadGuards]);

  const filteredRequests = useMemo(() => {
    if (!normalisedGuardQuery) return requests;
    return requests.filter((r) => normaliseSearch(r.guard_name).includes(normalisedGuardQuery));
  }, [requests, normalisedGuardQuery]);

  const summary = useMemo(() => {
    const pending = requests.filter((r) => r.status === 'pending').length;
    const approved = requests.filter((r) => r.status === 'approved').length;
    const denied = requests.filter((r) => r.status === 'denied').length;
    const cancelled = requests.filter((r) => r.status === 'cancelled').length;
    return { total: requests.length, pending, approved, denied, cancelled };
  }, [requests]);

  const handleApprove = useCallback(async (requestId: string) => {
    setActionInFlight(requestId);
    try {
      const editedNotes = editingNotesId === requestId ? editingNotesValue : undefined;
      await leaveService.approveLeaveRequest(requestId, editedNotes);
      setEditingNotesId(null);
      setEditingNotesValue('');
      await loadRequests();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setActionInFlight(null);
    }
  }, [editingNotesId, editingNotesValue, loadRequests]);

  const handleDeny = useCallback(async (requestId: string) => {
    setActionInFlight(requestId);
    try {
      const editedNotes = editingNotesId === requestId ? editingNotesValue : undefined;
      await leaveService.denyLeaveRequest(requestId, editedNotes);
      setEditingNotesId(null);
      setEditingNotesValue('');
      await loadRequests();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setActionInFlight(null);
    }
  }, [editingNotesId, editingNotesValue, loadRequests]);

  const handleSubmitLeave = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formGuardId) {
      setError(t('Please select a guard'));
      return;
    }
    if (formStartDate > formEndDate) {
      setError(t('End date must be on or after start date'));
      return;
    }

    setFormSubmitting(true);
    setError('');
    setFormSuccess('');

    try {
      await leaveService.createLeaveRequest({
        guardId: formGuardId,
        leaveType: formLeaveType,
        startDate: formStartDate,
        endDate: formEndDate,
        reason: formReason || undefined,
        adminNotes: formAdminNotes || undefined,
      });

      const guardName = guards.find((g) => g.id === formGuardId)?.full_name ?? 'Guard';
      setFormSuccess(t('{type} for {name} has been created and approved.', {
        type: t(LEAVE_TYPE_LABELS[formLeaveType]),
        name: guardName,
      }));
      setFormGuardId('');
      setFormLeaveType('annual');
      setFormStartDate(todayDateKey());
      setFormEndDate(todayDateKey());
      setFormReason('');
      setFormAdminNotes('');

      // Refresh the requests list
      void loadRequests();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setFormSubmitting(false);
    }
  }, [formGuardId, formLeaveType, formStartDate, formEndDate, formReason, formAdminNotes, guards, loadRequests]);

  const startEditNotes = (request: LeaveRequest) => {
    setEditingNotesId(request.id);
    setEditingNotesValue(request.admin_notes ?? '');
  };

  return (
    <div className="leave-page">
      <div className="leave-page__header">
        <div className="leave-page__intro">
          <span className="leave-page__eyebrow">{t('Schedule leave')}</span>
          <h1 className="leave-page__title">{t('Leave & Availability')}</h1>
          <p className="leave-page__subtitle">
            {t('Review and action guard leave requests, or manually add leave entries for vacation, sickness, or other absences.')}
          </p>
        </div>

        <div className="leave-page__controls">
          <div className="leave-page__control-group leave-page__control-group--toggle">
            <button
              type="button"
              className={`leave-page__toggle-btn${activeTab === 'requests' ? ' leave-page__toggle-btn--active' : ''}`}
              onClick={() => setActiveTab('requests')}
            >
              {t('Requests')}
            </button>
            <button
              type="button"
              className={`leave-page__toggle-btn${activeTab === 'add' ? ' leave-page__toggle-btn--active' : ''}`}
              onClick={() => setActiveTab('add')}
            >
              {t('Add Leave')}
            </button>
          </div>

          {activeTab === 'requests' && (
            <>
              <div className="leave-page__control-group">
                <label className="leave-page__field">
                  <span>{t('Status')}</span>
                  <select
                    className="leave-page__select"
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value as LeaveStatus | '')}
                  >
                    {STATUS_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>{t(opt.label)}</option>
                    ))}
                  </select>
                </label>
              </div>

              <div className="leave-page__control-group">
                <label className="leave-page__field">
                  <span>{t('Guard Search')}</span>
                  <div className="leave-page__search-wrap">
                    <input
                      type="search"
                      className="leave-page__search"
                      value={guardQuery}
                      onChange={(e) => setGuardQuery(e.target.value)}
                      placeholder={t('Search by guard name')}
                    />
                    {guardQuery && (
                      <button
                        type="button"
                        className="leave-page__search-clear"
                        onClick={() => setGuardQuery('')}
                        aria-label={t('Clear search')}
                      >
                        x
                      </button>
                    )}
                  </div>
                </label>
              </div>
            </>
          )}
        </div>
      </div>

      {error && (
        <div className="leave-page__error">
          <span>{error}</span>
          <button type="button" onClick={() => setError('')}>x</button>
        </div>
      )}

      {activeTab === 'requests' && (
        <>
          <div className="leave-page__stats">
            <div className="leave-page__stat">
              <span>{t('Total')}</span>
              <strong>{summary.total}</strong>
            </div>
            <div className={`leave-page__stat${summary.pending > 0 ? ' leave-page__stat--warning' : ''}`}>
              <span>{t('Pending')}</span>
              <strong>{summary.pending}</strong>
            </div>
            <div className="leave-page__stat">
              <span>{t('Approved')}</span>
              <strong>{summary.approved}</strong>
            </div>
            <div className="leave-page__stat">
              <span>{t('Denied')}</span>
              <strong>{summary.denied}</strong>
            </div>
            <div className="leave-page__stat">
              <span>{t('Cancelled')}</span>
              <strong>{summary.cancelled}</strong>
            </div>
          </div>

          <div className="leave-page__board">
            {isLoading ? (
              <div className="leave-page__state">{t('Loading leave requests...')}</div>
            ) : filteredRequests.length === 0 ? (
              <div className="leave-page__empty">
                <h3>
                  {guardQuery
                    ? 'No requests match that search'
                    : statusFilter
                      ? `No ${statusFilter} leave requests`
                      : 'No leave requests yet'}
                </h3>
                <p>
                  {guardQuery
                    ? 'Try a different name or clear the search.'
                    : 'Leave requests submitted by guards will appear here for review.'}
                </p>
              </div>
            ) : (
              <div className="leave-page__request-grid">
                {filteredRequests.map((request) => {
                  const isPending = request.status === 'pending';
                  const isActioning = actionInFlight === request.id;
                  const isEditingNotes = editingNotesId === request.id;
                  const days = countDays(request.start_date, request.end_date);

                  return (
                    <article
                      key={request.id}
                      className={`leave-page__request-card${isPending ? ' leave-page__request-card--pending' : ''}`}
                    >
                      <div className="leave-page__request-head">
                        <div className="leave-page__request-guard">
                          <span
                            className="leave-page__avatar"
                            style={request.guard_avatar_url ? undefined : getAvatarAccentStyle(request.guard_id || request.guard_name)}
                          >
                            {request.guard_avatar_url
                              ? <img src={request.guard_avatar_url} alt={request.guard_name} />
                              : initials(request.guard_name)}
                          </span>
                          <div>
                            <h3 className="leave-page__request-name">{request.guard_name}</h3>
                            <span className="leave-page__request-time">{formatRelativeTime(request.created_at)}</span>
                          </div>
                        </div>

                        <span className={`leave-page__badge ${STATUS_BADGE_CLASS[request.status]}`}>
                          {request.status}
                        </span>
                      </div>

                      <div className="leave-page__request-body">
                        <div className="leave-page__request-meta">
                          <span className="leave-page__type-chip">
                            {t(LEAVE_TYPE_LABELS[request.leave_type])}
                          </span>
                          <span className="leave-page__date-chip">
                            {formatDateRange(request.start_date, request.end_date)}
                          </span>
                          <span className="leave-page__days-chip">
                            {days} day{days !== 1 ? 's' : ''}
                          </span>
                        </div>

                        {request.reason && (
                          <p className="leave-page__request-reason">{request.reason}</p>
                        )}

                        {isPending && (
                          <div className="leave-page__notes-section">
                            {isEditingNotes ? (
                              <textarea
                                className="leave-page__notes-input"
                                value={editingNotesValue}
                                onChange={(e) => setEditingNotesValue(e.target.value)}
                                placeholder={t('Add a note (optional)...')}
                                rows={2}
                              />
                            ) : (
                              <button
                                type="button"
                                className="leave-page__add-note-btn"
                                onClick={() => startEditNotes(request)}
                              >
                                + Add note
                              </button>
                            )}
                          </div>
                        )}

                        {!isPending && request.admin_notes && (
                          <div className="leave-page__admin-note">
                            <span className="leave-page__admin-note-label">{t('Admin note')}</span>
                            <p>{request.admin_notes}</p>
                          </div>
                        )}

                        {!isPending && request.reviewer_name && (
                          <div className="leave-page__reviewed-by">
                            Reviewed by {request.reviewer_name}
                            {request.reviewed_at && ` · ${formatRelativeTime(request.reviewed_at)}`}
                          </div>
                        )}
                      </div>

                      {isPending && (
                        <div className="leave-page__request-actions">
                          <button
                            type="button"
                            className="leave-page__action-btn leave-page__action-btn--approve"
                            onClick={() => handleApprove(request.id)}
                            disabled={isActioning}
                          >
                            {isActioning ? 'Processing...' : 'Approve'}
                          </button>
                          <button
                            type="button"
                            className="leave-page__action-btn leave-page__action-btn--deny"
                            onClick={() => handleDeny(request.id)}
                            disabled={isActioning}
                          >
                            {isActioning ? 'Processing...' : 'Deny'}
                          </button>
                        </div>
                      )}
                    </article>
                  );
                })}
              </div>
            )}
          </div>
        </>
      )}

      {activeTab === 'add' && (
        <div className="leave-page__add-section">
          <div className="leave-page__add-card">
            <div className="leave-page__add-header">
              <h2>{t('Create leave entry')}</h2>
              <p>{t('Manually log leave for a guard. This will be auto-approved since it is entered by an admin.')}</p>
            </div>

            {formSuccess && (
              <div className="leave-page__success">
                <span>{formSuccess}</span>
                <button type="button" onClick={() => setFormSuccess('')}>x</button>
              </div>
            )}

            <form className="leave-page__form" onSubmit={handleSubmitLeave}>
              <label className="leave-page__form-field">
                <span>{t('Guard')}</span>
                <select
                  className="leave-page__form-select"
                  value={formGuardId}
                  onChange={(e) => setFormGuardId(e.target.value)}
                  required
                >
                  <option value="">{t('Select a guard...')}</option>
                  {guards.map((g) => (
                    <option key={g.id} value={g.id}>{g.full_name}</option>
                  ))}
                </select>
              </label>

              <label className="leave-page__form-field">
                <span>{t('Leave type')}</span>
                <select
                  className="leave-page__form-select"
                  value={formLeaveType}
                  onChange={(e) => setFormLeaveType(e.target.value as LeaveType)}
                >
                  {LEAVE_TYPE_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>{t(opt.label)}</option>
                  ))}
                </select>
              </label>

              <div className="leave-page__form-row">
                <label className="leave-page__form-field">
                  <span>{t('Start date')}</span>
                  <input
                    type="date"
                    className="leave-page__form-input"
                    value={formStartDate}
                    onChange={(e) => setFormStartDate(e.target.value)}
                    required
                  />
                </label>
                <label className="leave-page__form-field">
                  <span>{t('End date')}</span>
                  <input
                    type="date"
                    className="leave-page__form-input"
                    value={formEndDate}
                    onChange={(e) => setFormEndDate(e.target.value)}
                    required
                  />
                </label>
              </div>

              {formStartDate && formEndDate && formStartDate <= formEndDate && (
                <div className="leave-page__form-duration">
                  {countDays(formStartDate, formEndDate)} day{countDays(formStartDate, formEndDate) !== 1 ? 's' : ''} total
                </div>
              )}

              <label className="leave-page__form-field">
                <span>{t('Reason (optional)')}</span>
                <textarea
                  className="leave-page__form-textarea"
                  value={formReason}
                  onChange={(e) => setFormReason(e.target.value)}
                  placeholder={t('e.g. Family vacation, medical appointment...')}
                  rows={3}
                />
              </label>

              <label className="leave-page__form-field">
                <span>{t('Admin notes (optional)')}</span>
                <textarea
                  className="leave-page__form-textarea"
                  value={formAdminNotes}
                  onChange={(e) => setFormAdminNotes(e.target.value)}
                  placeholder={t('Internal notes visible only to admins...')}
                  rows={2}
                />
              </label>

              <button
                type="submit"
                className="leave-page__submit-btn"
                disabled={formSubmitting}
              >
                {formSubmitting ? 'Creating...' : 'Create leave entry'}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
